/**
 * shell/backstack.ts —— **返回栈协议**（A6①；102 真源 `src/shell/backstack.js`（239 行，
 * 现势版＝前端增量审计_20260922 快照）四层语义逐条落地，TypeScript 移植）。
 *
 * 为什么必须（102 backstack.js:5-6 逐条）：`<768px` 有四层可进入的层（左栏抽屉／右栏全屏／
 *   接管态／`shell.overlay`）；没有返回栈 ⇒ 返回键**直接退出页面**。
 *
 * 协议（与 102 逐条对应；行号＝102 真源）：
 *  ① **层级顺序**（102:31）：`overlay` → `takeover` → `rightbar-fullscreen` → `sidebar-drawer` →
 *     视图环/会话（`overlay` 最低＝最先被返回键关掉）。
 *  ② **机制**（102:11）：每层进入 `pushState`；退出由 `popstate` 消费；**Escape 与返回键走同一条栈**
 *     （禁两套）——本仓 Escape 关闭走壳层 `useOverlayFocus`（同步关状态＋焦点归还），栈侧由关闭函数
 *     `release`（＝`popTo` 收敛，102:178-188）走**同一条浏览器历史**，两路殊途同归。
 *  ③ **popstate 处理函数唯一**（102:12）：本模块是唯一注册处（`createBackStack` 内 102:209）。
 *  ④ **不写布局偏好**（102:13）：只操作历史栈，不落任何持久化偏好。
 *  ⑤ **栈＝浏览器历史的镜像**（102:14-20）：`popstate` 消费判据＝`event.state` 上的 `depth` 与
 *     `stack.length` **对账**（102:93-104），不是「state 是否本栈标记」。
 *  ⑥ **订阅签名 `cb(current, info)`**（102:21-23）：`info＝{action:'push'|'pop', layer, depth}`。
 *  ⑦ **前进亦对账**（102:24-25/105-120）：落点深度 > 栈长 ⇒ 恢复该层。
 *  ⑧ **`pushState` 合并既有 state**（102:26-27/157-160）：保留路由自家字段再叠加本栈标记。
 *  ⑨ **收尾须 `disposeBackStack()`**（102:28/236-239）：登出/卸载清栈，防污染下一会话。
 *  ★ 世代令牌（102:37-48/106-117）：「凭空恢复一层」分支做世代校验，防登出重登后恢复幽灵层；
 *    **刻意不拦弹出分支**（102:109-113）。
 *  ★ 收敛循环唯一推进点（102:126-145）：`popTo` 只发一次 `hist.back()`，其余各跳由 popstate 驱动。
 */

/** 层名（顺序即位次，由低到高；102 backstack.js:30-31） */
export type BackStackLayer = "overlay" | "takeover" | "rightbar-fullscreen" | "sidebar-drawer";

/** 层名表（102 backstack.js:31：`Object.freeze` 位次表） */
export const LAYERS: readonly BackStackLayer[] = Object.freeze([
  "overlay",
  "takeover",
  "rightbar-fullscreen",
  "sidebar-drawer",
]);

/** 历史 state 标记（102 backstack.js:34：路由层据此判定「这是层栈，不是路由跳转」） */
export const STATE_KEY = "sononuxLayer";

/**
 * 世代令牌的 state 字段名（102 backstack.js:45；★20260922 P1-16 幽灵层防御）。
 * 登出只清内存栈、清不掉浏览器历史里已写入的层记录 ⇒ 重登后凡带世代号且 ≠ 本世代者一律忽略。
 */
export const GEN_KEY = "sononuxGen";

/** 世代号：每次 `createBackStack` 递增（102 backstack.js:48） */
let _generation = 0;

/** 订阅回执（102 backstack.js:21-23 协议⑥：`info` 识别本次被消费的是哪一层） */
export interface BackStackInfo {
  action: "push" | "pop";
  layer: BackStackLayer;
  depth: number;
}

export type BackStackCallback = (current: BackStackLayer | null, info: BackStackInfo) => void;

/** 注入面（102 backstack.js:67：测试用假 history/window；jsdom 与真实浏览器同构） */
export interface BackStackEnv {
  history?: BackStackHistory;
  win?: BackStackWindow;
}

/** 最小 history 形状（真实 `History` 的子集；102 backstack.js:70） */
export interface BackStackHistory {
  readonly state: unknown;
  pushState(data: unknown, unused: string, url?: string | null): void;
  back(): void;
}

/** 最小 window 形状（仅 popstate 注册面；102 backstack.js:71/204/209） */
export interface BackStackWindow {
  addEventListener(type: "popstate", listener: (event: { state: unknown }) => void): void;
  removeEventListener(type: "popstate", listener: (event: { state: unknown }) => void): void;
}

interface PopStateLike {
  state: unknown;
}

/**
 * 判定某 history.state 是否由本栈产生（102 backstack.js:54-56：路由层消费）。
 */
export function isSononuxLayer(state: unknown): boolean {
  return Boolean(
    state && typeof state === "object" && typeof (state as Record<string, unknown>)[STATE_KEY] === "string",
  );
}

/** 从一条历史记录读出「层深度」（非本栈记录 ⇒ 0；102 backstack.js:59-63） */
function depthOfState(state: unknown): number {
  if (!isSononuxLayer(state)) return 0;
  const d = Number((state as Record<string, unknown>).depth);
  return Number.isFinite(d) && d > 0 ? Math.floor(d) : 0;
}

export interface BackStack {
  readonly LAYERS: readonly BackStackLayer[];
  push(layer: BackStackLayer): boolean;
  pop(): boolean;
  popTo(depth: number): boolean;
  current(): BackStackLayer | null;
  depth(): number;
  depthOf(layer: BackStackLayer): number;
  subscribe(cb: BackStackCallback): () => boolean;
  dispose(): void;
  isTop(layer: BackStackLayer): boolean;
}

/**
 * 建返回栈实例（102 backstack.js:69-224 逐段移植）。
 */
export function createBackStack(env: BackStackEnv = {}): BackStack {
  const hist: BackStackHistory = env.history ?? (globalThis.history as BackStackHistory);
  const win: BackStackWindow = env.win ?? (globalThis as unknown as BackStackWindow);
  const stack: BackStackLayer[] = [];
  const subs = new Set<BackStackCallback>();
  let disposed = false;
  const generation = ++_generation; // ★P1-16（102:75）：本实例世代号（登出后重建 ⇒ 换号）
  /** 收敛目标深度（非 null ⇒ 有一个「往目标退」的进行中收敛；102:77） */
  let pendingTarget: number | null = null;

  /** 通知订阅者（102:80-85；订阅方异常不级联） */
  function emit(info: BackStackInfo): void {
    const layer = current();
    subs.forEach((cb) => {
      try {
        cb(layer, info);
      } catch {
        /* 订阅方异常不级联（与多标签总线同纪律） */
      }
    });
  }

  /**
   * 历史位置变化 ⇒ **对账**（102:93-123，协议⑤⑦）：栈长必须等于当前历史位置的层深度。
   * 后退：落点更浅 ⇒ 逐层弹出并 emit；前进：落点更深 ⇒ 恢复一层并 emit；相等 ⇒ 幂等不动。
   */
  function onPopState(event: PopStateLike): void {
    if (disposed) return;
    const state = event ? event.state : null;
    const target = depthOfState(state);
    if (target < stack.length) {
      while (stack.length > target) {
        const popped = stack.pop() as BackStackLayer;
        emit({ action: "pop", layer: popped, depth: stack.length });
      }
      continueConvergence();
      return;
    }
    if (target > stack.length) {
      // ★幽灵层防御（102:106-117）：只在「凭空恢复一层」分支做世代校验；弹出分支刻意不拦。
      const stateGen = Number(state && (state as Record<string, unknown>)[GEN_KEY]);
      if (Number.isFinite(stateGen) && stateGen !== generation) {
        pendingTarget = null;
        return;
      }
      const layer = (state as Record<string, unknown>)[STATE_KEY] as BackStackLayer;
      if (!LAYERS.includes(layer)) {
        pendingTarget = null;
        return;
      }
      stack.push(layer);
      emit({ action: "push", layer, depth: stack.length });
    }
    continueConvergence();
  }

  /**
   * 收敛循环唯一推进点（102:137-145，★P2）：每次只发**一个** `hist.back()`，由真实 popstate
   * 驱动下一跳——防真实浏览器合并/丢事件。
   */
  function continueConvergence(): boolean {
    if (disposed || pendingTarget === null) return false;
    if (stack.length > pendingTarget) {
      hist.back();
      return true;
    }
    pendingTarget = null;
    return false;
  }

  function current(): BackStackLayer | null {
    return stack.length ? stack[stack.length - 1] : null;
  }

  /** 进入一层：入栈 ＋ pushState（102:151-163；协议⑧合并既有 state ＋ 世代号） */
  function push(layer: BackStackLayer): boolean {
    if (disposed) return false;
    if (!LAYERS.includes(layer)) {
      throw new TypeError(`backstack.push: 未知层 ${layer}（合法层：${LAYERS.join("/")}）`);
    }
    const prev = hist.state && typeof hist.state === "object" ? (hist.state as Record<string, unknown>) : {};
    stack.push(layer);
    hist.pushState({ ...prev, [STATE_KEY]: layer, depth: stack.length, [GEN_KEY]: generation }, "");
    emit({ action: "push", layer, depth: stack.length });
    return true;
  }

  /** 退出顶层：**走浏览器历史**（102:166-170：保证返回键与 Escape 同路径）；栈在 popstate 中弹出 */
  function pop(): boolean {
    if (disposed || stack.length === 0) return false;
    hist.back();
    return true;
  }

  /**
   * 收敛到指定深度（102:178-188）：关闭的层不在栈顶时其上各层一并弹出——历史镜像下无法只摘
   * 中间一层；每层分别 emit `pop`。只发一次 `hist.back()`（★P2），其余各跳由 popstate 驱动。
   */
  function popTo(depth: number): boolean {
    if (disposed) return false;
    const d = Math.max(0, Math.floor(Number(depth) || 0));
    const n = stack.length - d;
    if (n <= 0) return false;
    pendingTarget = d;
    hist.back();
    return true;
  }

  /** 某层在栈中的位置（-1＝不在栈中；102:191-193） */
  function depthOf(layer: BackStackLayer): number {
    return stack.indexOf(layer);
  }

  function subscribe(cb: BackStackCallback): () => boolean {
    subs.add(cb);
    return () => subs.delete(cb);
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    pendingTarget = null; // ★（102:203）：收尾清收敛目标
    win.removeEventListener("popstate", onPopState);
    subs.clear();
    stack.length = 0;
  }

  win.addEventListener("popstate", onPopState);

  return Object.freeze({
    LAYERS,
    push,
    pop,
    popTo,
    current,
    depth: () => stack.length,
    depthOf,
    subscribe,
    dispose,
    isTop: (layer: BackStackLayer) => current() === layer,
  });
}

// ── 单例面（102:226-239；**唯一 popstate 注册点**）：整个应用共用一条栈 ──
let _singleton: BackStack | null = null;

/** 取全局返回栈（首次调用时建立；`dispose` 后再次调用会重建；102:230-233） */
export function getBackStack(env?: BackStackEnv): BackStack {
  if (_singleton === null || env) _singleton = createBackStack(env);
  return _singleton;
}

/** 释放全局返回栈（**登出/卸载收尾必须调用**——协议⑨；102:236-239） */
export function disposeBackStack(): void {
  if (_singleton) _singleton.dispose();
  _singleton = null;
}
